import { useState, useCallback, useEffect } from "react";
import UIkit from "uikit";
import feather from "feather-icons";
import CRMService from "../../services/crm.service";

const FeatherIcon = ({ name, size = 16, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

const ACTIVITY_ICONS = {
  note: "file-text",
  task: "check-square",
  call: "phone",
  email: "mail",
  event: "calendar",
  text_message: "message-square",
  contact_created: "user-plus",
};

const ACTIVITY_LABELS = {
  note: "Note",
  task: "Task",
  call: "Call",
  email: "Email",
  event: "Event",
  text_message: "Message",
  contact_created: "Created",
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatDuration = (seconds) => {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ─── Resolve entity_type + entity_id from a CRM contact ───
const getEntity = (contact) => {
  if (!contact) return { entityType: null, entityId: null };
  if (contact._type === "unit") {
    return { entityType: "unit", entityId: contact.rental_id };
  }
  if (contact._type === "guest") {
    return {
      entityType: "guest",
      entityId: contact.guest_id || contact.crm_identity_id || contact.id,
    };
  }
  const cat = (contact.category || "").toLowerCase();
  const entityType = cat === "lead" || cat === "leads" ? "lead" : "contact";
  return { entityType, entityId: contact.contact_id || contact.id };
};

const DEFAULT_MODE = "activity";
const TIMELINE_PAGE_SIZE = 50;

const normalizeActivityType = (item) => {
  const raw = String(item?.activity_type || item?.type || "").toLowerCase();
  if (raw === "call" || raw === "phone_call" || raw === "phone-call") return "call";
  if (raw === "email" || raw === "crm_email" || raw === "mail") return "email";
  if (raw === "text_message" || raw === "text" || raw === "message" || raw === "sms" || raw === "sms_message") return "text_message";
  if (raw === "contact_created" || raw === "contact-created" || raw === "created") return "contact_created";
  if (raw === "note") return "note";
  if (raw === "event") return "event";
  if (raw === "task") return "task";
  if (!raw && item?.sender_type && item?.content) return "text_message";
  return raw || "note";
};

const filterItemsForMode = (mode, items) => {
  if (mode === "note") {
    return items.filter((i) => normalizeActivityType(i) === "note");
  }
  if (mode === "event") {
    return items.filter((i) => normalizeActivityType(i) === "event");
  }
  // activity mode: calls + texts + system-created events only
  return items.filter((i) => (
    normalizeActivityType(i) === "call"
    || normalizeActivityType(i) === "email"
    || normalizeActivityType(i) === "text_message"
    || normalizeActivityType(i) === "contact_created"
  ));
};

// ─── Activity Card ───
const ActivityCard = ({ item, onEdit, onDelete }) => {
  const icon = ACTIVITY_ICONS[item.activity_type] || "activity";
  const label = ACTIVITY_LABELS[item.activity_type] || item.activity_type;
  const isMessage = item.activity_type === "text_message" || item.activity_type === "contact_created";

  return (
    <div className={`crm-activity-card ${item.activity_type || ""}`}>
      <div className={`crm-activity-card-icon ${item.activity_type}`}>
        <FeatherIcon name={icon} size={14} />
      </div>
      <div className="crm-activity-card-body">
        <div className="crm-activity-card-header">
          <span className="crm-activity-card-type">{label}</span>
          {item.sender_type && (
            <span className={`crm-activity-card-direction ${item.sender_type}`}>
              {item.sender_type === "recipient" ? "← Inbound" : item.sender_type === "user" ? "→ Outbound" : item.sender_type === "automated" ? "→ Auto" : ""}
            </span>
          )}
        </div>
        <div className="crm-activity-card-title">
          {item.title || item.content || "(no content)"}
        </div>
        {item.description && (
          <div className="crm-activity-card-desc">{item.description}</div>
        )}
        {/* Task meta */}
        {item.activity_type === "task" && (
          <div className="crm-activity-card-meta">
            {item.status && (
              <span className={`crm-activity-status ${item.status}`}>
                {item.status}
              </span>
            )}
            {item.due_at && (
              <span className="crm-activity-due">Due {formatDate(item.due_at)}</span>
            )}
          </div>
        )}
        {/* Call meta */}
        {item.activity_type === "call" && (
          <div className="crm-activity-card-meta">
            {item.direction && (
              <span className="crm-activity-direction">{item.direction}</span>
            )}
            {item.outcome && (
              <span className="crm-activity-outcome">{item.outcome}</span>
            )}
            {item.duration_seconds > 0 && (
              <span className="crm-activity-duration">{formatDuration(item.duration_seconds)}</span>
            )}
          </div>
        )}
        {item.activity_type === "email" && (
          <div className="crm-activity-card-meta">
            {item.direction && (
              <span className="crm-activity-direction">{item.direction}</span>
            )}
            {item.to_email && (
              <span className="crm-activity-outcome">To: {item.to_email}</span>
            )}
            {item.cc_email && (
              <span className="crm-activity-outcome">CC: {item.cc_email}</span>
            )}
          </div>
        )}
        {/* Event meta */}
        {item.activity_type === "event" && item.event_date && (
          <div className="crm-activity-card-meta">
            <span className="crm-activity-event-date">
              <FeatherIcon name="calendar" size={12} /> {formatDate(item.event_date)}
            </span>
          </div>
        )}
        <div className="crm-activity-card-time">{formatDateTime(item.created_at)}</div>
      </div>
      <div className="crm-activity-card-actions">
        {onEdit && !isMessage && (
          <button className="crm-activity-action-btn" onClick={() => onEdit(item)} uk-tooltip="Edit">
            <FeatherIcon name="edit-2" size={13} />
          </button>
        )}
        {onDelete && !isMessage && (
          <button className="crm-activity-action-btn" onClick={() => onDelete(item)} uk-tooltip="Delete">
            <FeatherIcon name="trash-2" size={13} />
          </button>
        )}
      </div>
    </div>
  );
};

const TimelineMessage = ({ content }) => {
  const [expanded, setExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const textRef = useCallback((node) => {
    if (node) {
      setIsClamped(node.scrollHeight > node.clientHeight);
    }
  }, [content]);

  return (
    <>
      <div
        ref={textRef}
        className={`crm-timeline-text ${expanded ? "expanded" : "clamped"}`}
      >
        {content}
      </div>
      {isClamped && !expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(true)}
        >
          Show more
        </button>
      )}
      {expanded && (
        <button
          className="crm-timeline-show-more"
          onClick={() => setExpanded(false)}
        >
          Show less
        </button>
      )}
    </>
  );
};

const ActivityTimelineItem = ({ item, onEdit, onDelete }) => {
  const isMessage = item.activity_type === "text_message" || item.activity_type === "contact_created";
  const directionClass = item.activity_type === "email"
    ? (item.direction === "inbound" ? "inbound" : "outbound")
    : (item.sender_type === "recipient"
      ? "inbound"
      : (item.sender_type === "user" || item.sender_type === "automated" ? "outbound" : ""));

  const title = item.activity_type === "call"
    ? (item.title || item.summary || "Call")
    : item.activity_type === "email"
      ? (item.subject || item.title || "Email")
    : (item.title || item.content || "");

  return (
    <div className={`crm-timeline-item ${item.activity_type || ""} ${directionClass}`}>
      <div className="crm-timeline-line" />
      <div className={`crm-timeline-icon ${item.activity_type || "note"}`}>
        <FeatherIcon name={ACTIVITY_ICONS[item.activity_type] || "bell"} size={14} />
      </div>
      <div className="crm-timeline-content">
        <div className="crm-timeline-header">
          <span className="crm-timeline-sender">
            {item.activity_type === "call"
              ? "Call"
              : item.activity_type === "email"
                ? "Email"
                : item.activity_type === "contact_created"
                  ? "System"
                  : "Message"}
          </span>
        </div>
        {item.activity_type === "call" ? (
          <div className="crm-timeline-text expanded">{title}</div>
        ) : (
          <TimelineMessage content={title} />
        )}
        {item.activity_type === "email" && item.body && (
          <TimelineMessage content={item.body} />
        )}
        {item.activity_type === "call" && (
          <div className="crm-activity-card-meta">
            {item.direction && <span className="crm-activity-direction">{item.direction}</span>}
            {item.outcome && <span className="crm-activity-outcome">{item.outcome}</span>}
            {item.duration_seconds > 0 && (
              <span className="crm-activity-duration">{formatDuration(item.duration_seconds)}</span>
            )}
          </div>
        )}
        {item.activity_type === "email" && (
          <div className="crm-activity-card-meta">
            {item.direction && <span className="crm-activity-direction">{item.direction}</span>}
            {item.to_email && <span className="crm-activity-outcome">To: {item.to_email}</span>}
            {item.cc_email && <span className="crm-activity-outcome">CC: {item.cc_email}</span>}
          </div>
        )}
        <div className="crm-timeline-time">{formatDateTime(item.sent_at || item.created_at)}</div>
      </div>
      {!isMessage && (
        <div className="crm-activity-card-actions">
          {onEdit && (
            <button className="crm-activity-action-btn" onClick={() => onEdit(item)} uk-tooltip="Edit">
              <FeatherIcon name="edit-2" size={13} />
            </button>
          )}
          {onDelete && (
            <button className="crm-activity-action-btn" onClick={() => onDelete(item)} uk-tooltip="Delete">
              <FeatherIcon name="trash-2" size={13} />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main CRM Activity Panel ───
const CRMActivityPanel = ({ contact, mode = DEFAULT_MODE }) => {
  const isUnit = contact?._type === "unit";
  const isGuest = contact?._type === "guest";
  const isContact = !isUnit && !isGuest;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [timelinePage, setTimelinePage] = useState(0);
  const [hasMoreTimeline, setHasMoreTimeline] = useState(false);

  // Offcanvas state
  const [formType, setFormType] = useState(null); // note, task, call, email, event
  const [editingItem, setEditingItem] = useState(null);

  const { entityType, entityId } = getEntity(contact);
  // CRM activity tables historically store lead records under "contact"
  const activityEntityType = entityType === "lead" ? "contact" : entityType;

  const dedupeSort = useCallback((arr) => {
    const uniq = [];
    const seen = new Set();
    arr.forEach((item) => {
      const normalizedType = normalizeActivityType(item);
      const key = `${normalizedType}-${item.id || item.created_at}-${item.title || item.content || ""}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniq.push({ ...item, activity_type: normalizedType });
      }
    });
    uniq.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return uniq;
  }, []);

  // Fetch data based on mode
  const fetchData = useCallback(async () => {
    if (!activityEntityType || !entityId) return;
    if (timelinePage === 0 || mode !== "activity") {
      setLoading(true);
    }
    try {
      if (mode === "note") {
        const res = await CRMService.getNotes(activityEntityType, entityId);
        const data = res?.data || [];
        setItems(data.map((n) => ({ ...n, activity_type: "note", title: n.body })));
        setLoading(false);
        return;
      }

      if (mode === "event") {
        const res = await CRMService.getEvents(activityEntityType, entityId);
        const data = res?.data || [];
        setItems(data.map((e) => ({ ...e, activity_type: "event" })));
        setLoading(false);
        return;
      }

      // activity mode
      let calls = [];
      let emails = [];
      if (timelinePage === 0 || !isContact) {
        const callsRes = await CRMService.getCalls(activityEntityType, entityId);
        calls = (callsRes?.data || []).map((c) => ({
          ...c,
          activity_type: normalizeActivityType({ activity_type: c.activity_type || "call" }),
          title: c.summary,
        }));

        const emailsRes = await CRMService.getEmails(activityEntityType, entityId);
        emails = (emailsRes?.data || []).map((e) => ({
          ...e,
          activity_type: normalizeActivityType({ activity_type: e.activity_type || "email" }),
          title: e.subject,
        }));
      }

      let tlItems = [];

      if (isContact || isGuest) {
        try {
          const timelineId = isGuest
            ? (contact.guest_id || contact.id)
            : (contact.contact_id || contact.id);
          const fetchTimeline = isGuest
            ? CRMService.getGuestTimeline
            : CRMService.getContactTimeline;

          const tlRes = await fetchTimeline(timelineId, {
            per: TIMELINE_PAGE_SIZE,
            page: timelinePage,
          });
          tlItems = (tlRes?.data || []).map((t) => ({
            ...t,
            activity_type: normalizeActivityType({ type: t.type, sender_type: t.sender_type, content: t.content }),
            title: t.content,
          }));
          setHasMoreTimeline(tlItems.length === TIMELINE_PAGE_SIZE);
        } catch (_) {
          // show CRM activities only if timeline fetch fails
          setHasMoreTimeline(false);
        }
      } else {
        setHasMoreTimeline(false);
      }

      if (timelinePage === 0) {
        const next = dedupeSort([...calls, ...emails, ...tlItems]);
        setItems(filterItemsForMode("activity", next));
      } else {
        setItems((prev) => {
          const next = dedupeSort([...prev, ...tlItems]);
          return filterItemsForMode("activity", next);
        });
      }
    } catch (err) {
      console.error("Failed to fetch CRM activities", err);
      setItems([]);
    }
    setLoading(false);
  }, [activityEntityType, entityId, isContact, contact, mode, timelinePage, dedupeSort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setTimelinePage(0);
  }, [contact?.id, contact?.contact_id, contact?.guest_id, contact?.rental_id, mode]);

  const openForm = useCallback((type, item = null) => {
    setFormType(type);
    setEditingItem(item);
    // Small delay to ensure state is set before UIkit opens
    setTimeout(() => UIkit.offcanvas("#offcanvas-crm-activity-form").show(), 10);
  }, []);

  const handleEdit = useCallback((item) => {
    openForm(item.activity_type, item);
  }, [openForm]);

  const handleDelete = useCallback(async (item) => {
    try {
      if (item.activity_type === "note") await CRMService.deleteCrmNote(item.id);
      else if (item.activity_type === "call") await CRMService.deleteCall(item.id);
      else if (item.activity_type === "email") await CRMService.deleteEmail(item.id);
      else if (item.activity_type === "event") await CRMService.deleteEvent(item.id);
      // Optimistic remove
      setItems((prev) => prev.filter((i) => !(i.id === item.id && i.activity_type === item.activity_type)));
    } catch (err) {
      console.error("Failed to delete activity", err);
      UIkit.notification("Failed to delete", "error");
    }
  }, []);

  const handleFormSave = useCallback(async (type, data, isEdit) => {
    if (!activityEntityType || !entityId) return;
    try {
      const payload = { ...data, entity_type: activityEntityType, entity_id: entityId };
      let res;
      if (type === "note") {
        res = isEdit
          ? await CRMService.updateCrmNote(data.id, { body: data.body })
          : await CRMService.createCrmNote(payload);
      } else if (type === "call") {
        res = isEdit
          ? await CRMService.updateCall(data.id, data)
          : await CRMService.createCall(payload);
      } else if (type === "email") {
        res = isEdit
          ? await CRMService.updateEmail(data.id, data)
          : await CRMService.createEmail(payload);
      } else if (type === "event") {
        res = isEdit
          ? await CRMService.updateEvent(data.id, data)
          : await CRMService.createEvent(payload);
      }

      const saved = res?.data;
      if (saved) {
        // Normalize the saved item
        let normalized = { ...saved, activity_type: type };
        if (type === "note") normalized.title = saved.body;
        if (type === "call") normalized.title = saved.summary;
        if (type === "email") normalized.title = saved.subject;

        if (isEdit) {
          setItems((prev) =>
            prev.map((i) => (i.id === saved.id && i.activity_type === type ? normalized : i)),
          );
        } else {
          setItems((prev) => [normalized, ...prev]);
        }
      }

      UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
      UIkit.notification(isEdit ? "Updated" : "Added", "success");
    } catch (err) {
      console.error("Failed to save activity", err);
      UIkit.notification("Failed to save", "error");
    }
  }, [activityEntityType, entityId]);

  const canAddCall = mode === "activity" && !isUnit;
  const canAddEmail = mode === "activity" && !isUnit;
  const canAddNote = mode === "note";
  const canAddEvent = mode === "event" && !isUnit;

  return (
    <div className="crm-activity-panel">
      {/* Add button */}
      <div className="crm-activity-add-bar">
        {canAddCall && (
          <button className="uk-button hms-btn hms-white-btn uk-button-small" onClick={() => openForm("call")}>
            <FeatherIcon name="plus" size={14} /> Call
          </button>
        )}
        {canAddEmail && (
          <button className="uk-button hms-btn hms-white-btn uk-button-small" onClick={() => openForm("email")}>
            <FeatherIcon name="plus" size={14} /> Email
          </button>
        )}
        {canAddNote && (
          <button className="uk-button hms-btn hms-white-btn uk-button-small" onClick={() => openForm("note")}>
            <FeatherIcon name="plus" size={14} /> Add Note
          </button>
        )}
        {canAddEvent && (
          <button className="uk-button hms-btn hms-white-btn uk-button-small" onClick={() => openForm("event")}>
            <FeatherIcon name="plus" size={14} /> Add Event
          </button>
        )}
      </div>

      {/* List */}
      {loading && <div className="crm-list-empty">Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="crm-list-empty">
          {mode === "note"
            ? "No notes recorded yet"
            : mode === "event"
              ? "No events recorded yet"
              : "No activity recorded yet"}
        </div>
      )}
      {!loading && mode === "activity" && (
        <div className="crm-timeline">
          {items.map((item, i) => (
            <ActivityTimelineItem
              key={`${item.activity_type}-${item.id || i}`}
              item={item}
              onEdit={() => handleEdit(item)}
              onDelete={() => handleDelete(item)}
            />
          ))}
          {(isContact || isGuest) && hasMoreTimeline && (
            <div className="crm-timeline-load-more">
              <button
                className="uk-button uk-button-default uk-button-small"
                onClick={() => setTimelinePage((p) => p + 1)}
              >
                Load older messages
              </button>
            </div>
          )}
        </div>
      )}
      {!loading && mode !== "activity" &&
        items.map((item, i) => (
          <ActivityCard
            key={`${item.activity_type}-${item.id || i}`}
            item={item}
            onEdit={() => handleEdit(item)}
            onDelete={() => handleDelete(item)}
          />
        ))}

      {/* Offcanvas Form */}
      <ActivityFormOffcanvas
        formType={formType}
        editingItem={editingItem}
        onSave={handleFormSave}
        onClose={() => {
          setFormType(null);
          setEditingItem(null);
        }}
      />
    </div>
  );
};

// ─── Offcanvas Form ───
const ActivityFormOffcanvas = ({ formType, editingItem, onSave, onClose }) => {
  const isEdit = !!editingItem;

  // Note state
  const [noteBody, setNoteBody] = useState("");

  // Task state
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskDueAt, setTaskDueAt] = useState("");
  const [taskStatus, setTaskStatus] = useState("open");

  // Call state
  const [callDirection, setCallDirection] = useState("outbound");
  const [callSummary, setCallSummary] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [callDuration, setCallDuration] = useState("");

  // Event state
  const [eventTitle, setEventTitle] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [eventDescription, setEventDescription] = useState("");

  // Email state
  const [emailDirection, setEmailDirection] = useState("outbound");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailCc, setEmailCc] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailSentAt, setEmailSentAt] = useState("");

  // Populate fields when editing
  useEffect(() => {
    if (editingItem) {
      if (editingItem.activity_type === "note" || formType === "note") {
        setNoteBody(editingItem.body || editingItem.title || "");
      }
      if (editingItem.activity_type === "task" || formType === "task") {
        setTaskTitle(editingItem.title || "");
        setTaskDescription(editingItem.description || "");
        setTaskDueAt(editingItem.due_at ? editingItem.due_at.split("T")[0] : "");
        setTaskStatus(editingItem.status || "open");
      }
      if (editingItem.activity_type === "call" || formType === "call") {
        setCallDirection(editingItem.direction || "outbound");
        setCallSummary(editingItem.summary || editingItem.title || "");
        setCallOutcome(editingItem.outcome || "");
        setCallDuration(editingItem.duration_seconds ? String(editingItem.duration_seconds) : "");
      }
      if (editingItem.activity_type === "email" || formType === "email") {
        setEmailDirection(editingItem.direction || "outbound");
        setEmailSubject(editingItem.subject || editingItem.title || "");
        setEmailTo(editingItem.to_email || "");
        setEmailCc(editingItem.cc_email || "");
        setEmailBody(editingItem.body || editingItem.description || "");
        setEmailSentAt(editingItem.sent_at ? editingItem.sent_at.slice(0, 16) : "");
      }
      if (editingItem.activity_type === "event" || formType === "event") {
        setEventTitle(editingItem.title || "");
        setEventDate(editingItem.event_date ? editingItem.event_date.split("T")[0] : "");
        setEventDescription(editingItem.description || "");
      }
    } else {
      resetAll();
    }
  }, [editingItem, formType]);

  const resetAll = () => {
    setNoteBody("");
    setTaskTitle("");
    setTaskDescription("");
    setTaskDueAt("");
    setTaskStatus("open");
    setCallDirection("outbound");
    setCallSummary("");
    setCallOutcome("");
    setCallDuration("");
    setEmailDirection("outbound");
    setEmailSubject("");
    setEmailTo("");
    setEmailCc("");
    setEmailBody("");
    setEmailSentAt("");
    setEventTitle("");
    setEventDate("");
    setEventDescription("");
  };

  const handleSubmit = () => {
    const type = formType || editingItem?.activity_type;
    if (!type) return;

    let data = {};
    if (type === "note") {
      if (!noteBody.trim()) {
        UIkit.notification("Note body is required", "warning");
        return;
      }
      data = { body: noteBody.trim() };
    } else if (type === "task") {
      if (!taskTitle.trim()) {
        UIkit.notification("Task title is required", "warning");
        return;
      }
      data = {
        title: taskTitle.trim(),
        description: taskDescription.trim(),
        due_at: taskDueAt || null,
        status: taskStatus,
      };
    } else if (type === "call") {
      if (!callSummary.trim()) {
        UIkit.notification("Call summary is required", "warning");
        return;
      }
      data = {
        summary: callSummary.trim(),
        direction: callDirection,
        outcome: callOutcome,
        duration_seconds: callDuration ? parseInt(callDuration, 10) : null,
      };
    } else if (type === "email") {
      if (!emailSubject.trim() && !emailBody.trim()) {
        UIkit.notification("Email subject or body is required", "warning");
        return;
      }
      data = {
        direction: emailDirection,
        subject: emailSubject.trim(),
        to_email: emailTo.trim(),
        cc_email: emailCc.trim(),
        body: emailBody.trim(),
        sent_at: emailSentAt || null,
      };
    } else if (type === "event") {
      if (!eventTitle.trim() || !eventDate) {
        UIkit.notification("Event title and date are required", "warning");
        return;
      }
      data = {
        title: eventTitle.trim(),
        event_date: eventDate,
        description: eventDescription.trim(),
      };
    }

    if (isEdit) data.id = editingItem.id;
    onSave(type, data, isEdit);
  };

  const activeType = formType || editingItem?.activity_type;
  const formTitle = isEdit
    ? `Edit ${ACTIVITY_LABELS[activeType] || "Activity"}`
    : `Add ${ACTIVITY_LABELS[activeType] || "Activity"}`;

  return (
    <div
      id="offcanvas-crm-activity-form"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={onClose}
          ></button>
          <div className="uk-modal-header">
            <h2 className="uk-modal-title">{formTitle}</h2>
          </div>
          <div className="uk-modal-body">
            {/* Note Form */}
            {activeType === "note" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Note</label>
                  <textarea
                    className="uk-textarea"
                    rows={5}
                    placeholder="Enter note…"
                    value={noteBody}
                    onChange={(e) => setNoteBody(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Call Form */}
            {activeType === "call" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Direction</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={callDirection}
                        onChange={(e) => setCallDirection(e.target.value)}
                      >
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{callDirection === "inbound" ? "Inbound" : "Outbound"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Summary</label>
                  <textarea
                    className="uk-textarea"
                    rows={3}
                    placeholder="Call summary"
                    value={callSummary}
                    onChange={(e) => setCallSummary(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Outcome</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={callOutcome}
                        onChange={(e) => setCallOutcome(e.target.value)}
                      >
                        <option value="">Select outcome…</option>
                        <option value="connected">Connected</option>
                        <option value="no_answer">No Answer</option>
                        <option value="left_voicemail">Left Voicemail</option>
                        <option value="busy">Busy</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{{"":"Select outcome…",connected:"Connected",no_answer:"No Answer",left_voicemail:"Left Voicemail",busy:"Busy"}[callOutcome] || "Select outcome…"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Duration (seconds)</label>
                  <input
                    className="uk-input"
                    type="number"
                    min="0"
                    placeholder="e.g. 120"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Event Form */}
            {activeType === "event" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Title</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="Event title"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Date</label>
                  <input
                    className="uk-input"
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Description</label>
                  <textarea
                    className="uk-textarea"
                    rows={3}
                    placeholder="Event description"
                    value={eventDescription}
                    onChange={(e) => setEventDescription(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Email Form */}
            {activeType === "email" && (
              <div>
                <div className="uk-margin">
                  <label className="uk-form-label">Direction</label>
                  <div className="uk-inline uk-width-1-1">
                    <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                      <select
                        value={emailDirection}
                        onChange={(e) => setEmailDirection(e.target.value)}
                      >
                        <option value="outbound">Outbound</option>
                        <option value="inbound">Inbound</option>
                      </select>
                      <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                        <span>{emailDirection === "inbound" ? "Inbound" : "Outbound"}</span>
                        <span uk-icon="icon: chevron-down"></span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Subject</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="Email subject"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">To</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="recipient@example.com"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">CC</label>
                  <input
                    className="uk-input"
                    type="text"
                    placeholder="cc@example.com"
                    value={emailCc}
                    onChange={(e) => setEmailCc(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Sent At</label>
                  <input
                    className="uk-input"
                    type="datetime-local"
                    value={emailSentAt}
                    onChange={(e) => setEmailSentAt(e.target.value)}
                  />
                </div>
                <div className="uk-margin">
                  <label className="uk-form-label">Body</label>
                  <textarea
                    className="uk-textarea"
                    rows={6}
                    placeholder="Email body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Save / Cancel */}
            <div className="crm-activity-form-actions uk-margin-top">
              <button
                type="button"
                className="uk-button hms-btn hms-red-btn"
                onClick={handleSubmit}
              >
                {isEdit ? "Update" : "Save"}
              </button>
              <button
                type="button"
                className="uk-button hms-btn hms-white-btn"
                onClick={() => {
                  UIkit.offcanvas("#offcanvas-crm-activity-form").hide();
                  onClose();
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMActivityPanel;
